import { spWarnTags } from "@eventr/common";
import { useConnectivityStore } from "../../feat/connectivity/connectivity.store";
import { bs } from "../bound.store";
import { BackoffOptions, createRetryLoop } from "./backoff";
import { isSocketConnectingOrOpen } from "./socket-state";

const RECONNECT_BACKOFF: BackoffOptions = { baseMs: 1000, capMs: 30000 };

/**
 * RocketChat's DDP server ping arrives well inside this window, so a socket that is still
 * `OPEN` but has sent nothing for this long is treated as dead.
 */
const LIVENESS_TIMEOUT_MS = 45000;

const reconnectLoop = createRetryLoop(
  RECONNECT_BACKOFF,
  (delayMs, attempt, reason) => `Reopening websocket in ${delayMs}ms, attempt ${attempt}, ${reason}`,
  (message) => spWarnTags(message, ["rocketchat"]),
);

let generation = 0;
let livenessTimer: ReturnType<typeof setTimeout> | null = null;

/** Claims a new socket id, invalidating the handlers of every socket opened before it. */
export function nextSocketGeneration(): number {
  generation += 1;
  return generation;
}

export function currentSocketGeneration(): number {
  return generation;
}

export function isCurrentSocketGeneration(id: number): boolean {
  return id === generation;
}

export function cancelReconnect(): void {
  reconnectLoop.cancel();
}

/** Returns the retry sequence to its first, shortest delay. */
export function resetReconnectBackoff(): void {
  reconnectLoop.reset();
}

/** Replaces any pending retry with one scheduled at the next backoff delay. */
export function scheduleReconnect(reason: string): void {
  reconnectLoop.scheduleNext(() => bs().openWebsocket(), reason);
}

export function stopLivenessWatchdog(): void {
  if (livenessTimer) {
    clearTimeout(livenessTimer);
    livenessTimer = null;
  }
}

/** Rearms the silence timeout. Called on socket open and on every inbound frame. */
export function noteSocketActivity(): void {
  stopLivenessWatchdog();
  livenessTimer = setTimeout(() => {
    livenessTimer = null;
    spWarnTags(`No websocket traffic for ${LIVENESS_TIMEOUT_MS}ms, recycling the socket`, ["rocketchat"]);
    bs().closeWebsocket(true, "socket-supervisor, liveness timeout");
    scheduleReconnect("liveness timeout");
  }, LIVENESS_TIMEOUT_MS);
}

// A restored connection retries at once rather than waiting out a backoff delay earned while
// the device was offline. Skipped if the socket is already connecting/open, so a bouncing
// connectivity signal doesn't tear down and replace an already-healthy connection.
useConnectivityStore.subscribe(
  (s) => s.isConnectivityAvailable,
  (isAvailable, wasAvailable) => {
    if (!isAvailable || wasAvailable) return;
    if (isSocketConnectingOrOpen(bs().rocketWebsocket)) return;
    resetReconnectBackoff();
    scheduleReconnect("connectivity restored");
  },
);
